import { useFormik } from 'formik';
import React, { useEffect, useState, useMemo } from 'react';
import { HiOutlinePlay } from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import { PiPackage, PiTruck, PiWarning } from 'react-icons/pi';
import { Button, Input, Text, Title } from 'rizzui';
import { toast } from 'sonner';
import moment from 'moment';
import CustomSelect from '../../../../components/forms/CustomSelect';
import ModalForm from '../../../../components/forms/ModalForm';
import useProducts from '../../../../hooks/api-calls/useProducts';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import { PickupService } from '../../../../services/pickup/pickupService';
import { Pickup } from '../../../../type/entities/pickup-type';
import { SuggestedProduct } from '../../../../type/entities/suggested-product-type';

interface StartDailyPickupsModalProps {
    routeGroups: any,
    refetchData: () => void,
    selectedDate?: string, // Añadimos la fecha seleccionada como prop
    showWarningOnly?: boolean, // Nueva prop para mostrar solo la advertencia
}

interface ProductDelivery {
    id: string;
    type: string;
    quantity: number;
}


const StartDailyPickupsModal: React.FC<StartDailyPickupsModalProps> = ({
    routeGroups,
    refetchData,
    selectedDate,
    showWarningOnly = false
}) => {
    // STATES
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [routesToInitialize, setRoutesToInitialize] = useState<Pickup[]>([]);
    const [recommendedProducts, setRecommendedProducts] = useState<SuggestedProduct[]>([]);

    // SERVICES
    const pickupService = new PickupService();
    const { handleErrors } = useHandleErrors();
    const { getProductsList } = useProducts();

    // VALIDACIÓN DE FECHA Y ESTADO DE RECOGIDAS
    const isDateBeforeToday = useMemo(() => {
        if (!selectedDate) return false;
        const today = moment().startOf('day');
        const selected = moment(selectedDate).startOf('day');
        return selected.isBefore(today);
    }, [selectedDate]);

    /**
     * Función que evalúa si se debe ocultar completamente el componente de INICIALIZACIÓN
     * Retorna true cuando NO se debe mostrar ningún aviso ni botón de INICIALIZAR
     * NOTA: Esto NO afecta al botón de finalizar jornada (FinishDailyPickupsModal)
     */
    const shouldHideComponent = useMemo(() => {
        if (!routeGroups || routeGroups.length === 0) return true;
        
        const allPickups = routeGroups.flatMap((group: any) => group.pickups);
        if (allPickups.length === 0) return true;
        
        // Verificar estados de todas las recogidas
        const pickupsByStatus = {
            planned: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'planned'),
            initialized: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'started_by_driver'),
            completed: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'completed'),
            failed: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'failed'),
            cancelled: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'cancelled'),
            rescheduled: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'rescheduled'),
            signaturePending: allPickups.filter((pickup: Pickup) => pickup.pickupStatus === 'signature_pending')
        };
        
        // SOLO ocultar el componente de inicialización si:
        // - TODAS las recogidas están en cualquier estado diferente a "Planificadas"
        // - Esto permite que el botón de finalizar jornada siga funcionando
        const allPickupsNotPlanned = pickupsByStatus.planned.length === 0;
        
        // Solo ocultar si no hay recogidas planificadas
        // El botón de finalizar jornada se maneja en FinishDailyPickupsModal
        return allPickupsNotPlanned;
    }, [routeGroups, selectedDate, isDateBeforeToday]);

    // Verificar si hay recogidas no inicializadas (planned) - Solo para uso interno
    const hasUnInitializedPickups = useMemo(() => {
        if (!routeGroups || routeGroups.length === 0) return false;
        return routeGroups.some((group: any) => 
            group.pickups.some((pickup: Pickup) => pickup.pickupStatus === 'planned')
        );
    }, [routeGroups]);

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES INICIAR RECOGIDAS DIARIAS
     * @EN START DAILY PICKUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleStartPickups = async (values: any) => {
        setLoading(true);
        const sendValues = {
            pickups: routesToInitialize.map((pickup: Pickup) => pickup.id),
            products: recommendedProducts.map((product: SuggestedProduct) => ({
                id: product.productId,
                name: product.productName,
                type: 'out',
                quantity: product.totalEstimatedQuantity,
            }))
        }
        try {
            const response = (await pickupService.startDailyPickups(sendValues)).getResponseData();

            if (response.success) {
                toast.success('Recogidas diarias iniciadas con éxito');
                refetchData();
                formik.resetForm();
            } else {
                handleErrors(response);
                formik.resetForm();
            }
        } catch (error) {
            handleErrors(error);
            formik.resetForm();
        } finally {
            setIsOpen(false);
            setLoading(false);
            formik.resetForm();
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // FORM
    const formik = useFormik({
        initialValues: {
            pickups: [] as Pickup[],
            products: [] as ProductDelivery[],
        },
        onSubmit: handleStartPickups,
    });


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER PRODUCTOS SUGERIDOS PARA RECOGIDAS DIARIAS
     * @EN GET SUGGESTED PRODUCTS FOR DAILY PICKUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (routeGroups && routeGroups.length > 0) {
            setRoutesToInitialize(routeGroups.flatMap((group: any) => group.pickups.filter((pickup: Pickup) => pickup.pickupStatus === 'planned')));
        }
    }, [routeGroups]);
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER PRODUCTOS SUGERIDOS PARA RECOGIDAS DIARIAS
     * @EN GET SUGGESTED PRODUCTS FOR DAILY PICKUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (routesToInitialize && routesToInitialize.length > 0) {
            setLoading(true);
            const headquarterIds = routesToInitialize.map((route) => route.headquarter.id);

            pickupService.getPickupSuggestedProductsQuantity(headquarterIds)
                .then((response: any) => {
                    const responseData = response.getResponseData() || [];
                    setRecommendedProducts(responseData.data);
                }).catch(() => {
                    toast.error('Error al obtener productos sugeridos');
                }).finally(() => {
                    setLoading(false);
                });
        }
    }, [routesToInitialize]);
    //-------------------------------------------------------------------------------------------------------------

    // Si no hay recogidas para inicializar y no está cargando, no mostrar nada
    if (!routesToInitialize && !loading) {
        return null;
    }

    // Si se debe ocultar el componente completamente, no mostrar nada
    if (shouldHideComponent) {
        return null;
    }

    // Si showWarningOnly es true, mostrar advertencia solo para fecha anterior con recogidas no inicializadas
    if (showWarningOnly) {
        // Solo mostrar aviso si es fecha anterior Y hay recogidas planificadas
        const shouldShowDateWarning = isDateBeforeToday && hasUnInitializedPickups;
        
        return shouldShowDateWarning ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                        <PiWarning className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                        <Title as="h4" className="text-amber-800 font-medium mb-2">
                            No se puede inicializar recogidas
                        </Title>
                        <Text className="text-amber-700 text-sm leading-relaxed">
                            No se puede inicializar recogidas al día anterior al de hoy, debe ponerse en contacto con su administrador para reprogramar la recogida a una nueva fecha.
                        </Text>
                        <div className="mt-3 pt-3 border-t border-amber-200">
                            <Text className="text-amber-600 text-xs font-medium">
                                Fecha seleccionada: {moment(selectedDate).format('DD/MM/YYYY')} | Fecha actual: {moment().format('DD/MM/YYYY')}
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        ) : null;
    }

    return (
        <>
            {/* Button to initialize daily pickups - NUNCA se muestra en días anteriores */}
            {!isDateBeforeToday && hasUnInitializedPickups && routesToInitialize.length > 0 && (
                <Button
                    onClick={() => setIsOpen(true)}
                    variant='solid'
                    color='primary'
                >
                    Inicializar Recogidas
                </Button>
            )}

            <ModalForm
                isOpen={isOpen && !showConfirmModal}
                title="Iniciar Recogidas Diarias"
                onClose={() => {
                    formik.resetForm();
                    setIsOpen(false)
                }}
                containerClassName="!max-w-4xl"
            >
                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <Text className="ml-3">Cargando recogidas...</Text>
                    </div>
                ) : (
                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                        {/* Información de recogidas */}
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center mb-3">
                                <PiTruck className="h-5 w-5 text-blue-600 mr-2" />
                                <Title as="h4" className="text-blue-900">
                                    Recogidas Planificadas ({routesToInitialize?.length || 0})
                                </Title>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {routesToInitialize.map((route) => (
                                    <div key={route.id} className="bg-white p-3 rounded border">
                                        <Text className="font-medium text-gray-900">
                                            {route.headquarter.name}
                                        </Text>
                                        <Text className="text-sm text-gray-600">
                                            {route.route?.name || 'Sin ruta asignada'}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Productos sugeridos */}
                        <div className="bg-white md:p-2 rounded-lg">
                            <div className="flex flex-col md:flex-row items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <PiPackage className="h-5 w-5 text-green-600 mr-2" />
                                    <Title as="h4" className="text-green-900">
                                        Productos para cargar en vehiculo
                                    </Title>
                                </div>
                                <Text className="text-sm text-gray-600">
                                    Puedes modificarlo según necesites
                                </Text>
                            </div>

                            {/* Add products selector with quantities, and default recommended products selected with default quantity */}
                            <CustomSelect
                                id="products"
                                label="Productos"
                                placeholder="Seleccionar productos"
                                className='bg-white'
                                containerClassName='mb-3'
                                options={getProductsList()}

                                value={
                                    getProductsList().filter((option: any) =>
                                        (recommendedProducts as SuggestedProduct[]).some((p) => p.productId === option.value)
                                    ) || null
                                }
                                onChange={(value: any) => {
                                    const selectedIds: string[] = value?.map((v: any) => v.value) || [];
                                    const current: SuggestedProduct[] = (recommendedProducts as SuggestedProduct[]) || [];
                                    const updated: SuggestedProduct[] = selectedIds.map((id) => {
                                        const existing = current.find((p) => p.productId === id);
                                        return existing ? existing : {
                                            productId: id,
                                            productName: value.find((v: any) => v.value === id)?.label || '',
                                            totalEstimatedQuantity: 0,
                                            type: 'out'
                                        };
                                    });
                                    setRecommendedProducts(updated);
                                }}
                                error={formik.errors.products}
                                isClearable={true}
                                isSearchable={true}
                                isLoading={!getProductsList()}
                                required={true}
                                isMulti={true}
                            />

                            {recommendedProducts.map((product: SuggestedProduct) => (
                                <div key={product.productId} className="flex items-center justify-between py-2 border-b">
                                    {/* Remove button */}
                                    <div className="flex items-center flex-row gap-3 ">
                                        <Button
                                            onClick={() => {
                                                setRecommendedProducts(
                                                    recommendedProducts.filter((p) => p.productId !== product.productId)
                                                );
                                            }}
                                            className="bg-red-500 text-white hover:bg-red-700 py-0 px-2 md:py-2 md:px-4"
                                        >
                                            <MdDelete className="h-5 w-5 text-white" />
                                        </Button>
                                        <Text className="text-gray-900 font-medium text-sm md:text-base">
                                            {product.productName}
                                        </Text>
                                    </div>
                                    <Input
                                        id={`quantity-${product.productId}`}
                                        type="number"
                                        defaultValue={product.totalEstimatedQuantity}
                                        onChange={(e) => {
                                            const newQuantity = Number(e.target.value);
     
                                            setRecommendedProducts(
                                                recommendedProducts.map((p) =>
                                                    p.productId === product.productId
                                                        ? { ...p, totalEstimatedQuantity: newQuantity }
                                                        : p
                                                )
                                            );
                                        }}
                                        className="w-16 text-center bg-white"
                                        min={1}
                                        required={true}
                                    />
                                </div>
                            ))}

                            {recommendedProducts.length === 0 && (
                                <div className="text-center py-8">
                                    <Text className="text-gray-500">
                                        No hay productos seleccionados
                                    </Text>
                                </div>
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex justify-end space-x-3 py-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsOpen(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading || recommendedProducts.length === 0}
                                isLoading={loading}
                                className="flex items-center"
                            >
                                <HiOutlinePlay className="h-4 w-4 mr-2" />
                                Iniciar Jornada
                            </Button>
                        </div>
                    </form>
                )}
            </ModalForm>

        </>
    );
};

export default StartDailyPickupsModal;